import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Button from "../../atoms/Button";
import AccountDeletionPublicLayout from "./PublicLayout";
import RecoveryStatusPanel, { isKnownRecoveryStatus } from "./RecoveryStatusPanel";
import { accountDeletionService } from "../../../services/accountDeletionService";
import {
    clearAccountDeletionSsoCallback,
    rememberAccountDeletionSsoCallback,
    navigateToAccountDeletionSsoProvider,
    requireSafeAccountDeletionAuthorizeUrl,
} from "../../../services/accountDeletionSsoCallback";
import { useSensitivePublicPage } from "../../../hooks/useSensitivePublicPage";
import { useAccountDeletionRecoverySession } from "../../../hooks/useAccountDeletionRecoverySession";
import { useAccountDeletionAnonymousSession } from "../../../hooks/useAccountDeletionAnonymousSession";
import { useAccountDeletionSsoProviders } from "../../../hooks/useAccountDeletionSsoProviders";
import { recoveryErrorKey } from "./recoveryUi";

const START_STEP = "start";
const CHALLENGE_STEP = "challenge";
const STATUS_STEP = "status";

const AccountDeletionRecoveryPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    useSensitivePublicPage(t("accountDeletionPublic.recovery.documentTitle"));
    const recoveryHandoffUnavailable = location.state?.recoveryAvailable === false;
    const isPublicSessionReady = useAccountDeletionAnonymousSession();
    const ssoProviders = useAccountDeletionSsoProviders(isPublicSessionReady);
    const recoverySession = useAccountDeletionRecoverySession();
    const [step, setStep] = useState(START_STEP);
    const [email, setEmail] = useState(() => {
        const recoveryEmail = location.state?.recoveryEmail;
        return typeof recoveryEmail === "string"
            ? recoveryEmail.trim().slice(0, 320)
            : "";
    });
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [challengeId, setChallengeId] = useState(null);
    const [genericStartAccepted, setGenericStartAccepted] = useState(false);
    const [errorKey, setErrorKey] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const emailInputRef = useRef(null);
    const codeInputRef = useRef(null);
    const statusRegionRef = useRef(null);

    useEffect(() => {
        if (!isPublicSessionReady) {
            return;
        }
        const focusTarget = step === START_STEP
            ? emailInputRef.current
            : step === CHALLENGE_STEP
                ? codeInputRef.current
                : statusRegionRef.current;
        try {
            focusTarget?.focus({ preventScroll: true });
        } catch (_) {
            focusTarget?.focus();
        }
    }, [isPublicSessionReady, step]);

    const acceptRecoveryCredentials = async (response) => {
        setCode("");
        setChallengeId(null);
        setStep(STATUS_STEP);
        await recoverySession.acceptCredentials(response);
    };

    const startRecovery = async (event) => {
        event.preventDefault();
        if (!isPublicSessionReady || !email.trim() || !password) {
            setErrorKey("accountDeletionPublic.recovery.errors.startProofRequired");
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const response = await accountDeletionService.startRecovery({ email, password });
            const nextChallengeId = typeof response?.challengeId === "string"
                ? response.challengeId.trim()
                : "";
            setGenericStartAccepted(true);
            setEmail("");
            setPassword("");
            if (nextChallengeId) {
                setChallengeId(nextChallengeId);
                setStep(CHALLENGE_STEP);
            }
        } catch (error) {
            setErrorKey(recoveryErrorKey(error));
        } finally {
            setPassword("");
            setIsSubmitting(false);
        }
    };

    const startRecoverySso = async (provider) => {
        if (!isPublicSessionReady
            || !email.trim()
            || !ssoProviders.includes(provider)) {
            setErrorKey("accountDeletionPublic.recovery.errors.startProofRequired");
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        clearAccountDeletionSsoCallback();
        try {
            const response = await accountDeletionService.startRecoverySso({ email, provider });
            const authorizeUrl = requireSafeAccountDeletionAuthorizeUrl(response?.authorizeUrl);
            rememberAccountDeletionSsoCallback({ flow: "recovery", provider });
            setEmail("");
            setPassword("");
            navigateToAccountDeletionSsoProvider(authorizeUrl);
        } catch (error) {
            clearAccountDeletionSsoCallback();
            setErrorKey(recoveryErrorKey(error));
            setIsSubmitting(false);
        }
    };

    const completeChallenge = async (event) => {
        event.preventDefault();
        if (!challengeId || !/^\d{6}$/.test(code)) {
            setErrorKey("accountDeletionPublic.recovery.errors.proofRequired");
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const response = await accountDeletionService.completeRecoveryChallenge({
                challengeId,
                code,
            });
            await acceptRecoveryCredentials(response);
        } catch (error) {
            setErrorKey(recoveryErrorKey(error));
        } finally {
            setCode("");
            setIsSubmitting(false);
        }
    };

    const refreshStatus = async () => {
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            await recoverySession.refreshStatus();
        } catch (error) {
            setErrorKey(recoveryErrorKey(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmRecovery = async () => {
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const nextStatus = await recoverySession.confirmRecovery();
            if (!isKnownRecoveryStatus(nextStatus?.status)) {
                setErrorKey("accountDeletionPublic.recovery.errors.generic");
            }
        } catch (error) {
            setErrorKey(recoveryErrorKey(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const isBusy = isSubmitting || !isPublicSessionReady;
    const showStartForm = step === START_STEP && !recoveryHandoffUnavailable;

    return (
        <AccountDeletionPublicLayout
            title={t("accountDeletionPublic.recovery.title")}
            description={t("accountDeletionPublic.recovery.description")}
            showRestrictedLink
        >
            {errorKey && (
                <div className="account-deletion-public-card__feedback account-deletion-public-card__feedback--error" role="alert">
                    {t(errorKey)}
                </div>
            )}

            {!isPublicSessionReady && (
                <div className="account-deletion-public-card__feedback" role="status" aria-live="polite">
                    {t("accountDeletionPublic.preparing")}
                </div>
            )}

            {genericStartAccepted && step === START_STEP && (
                <div className="account-deletion-public-card__feedback" role="status" aria-live="polite">
                    {t("accountDeletionPublic.recovery.genericStartAccepted")}
                </div>
            )}

            {recoveryHandoffUnavailable && step === START_STEP && (
                <div className="account-deletion-public-card__feedback account-deletion-public-card__feedback--error" role="alert">
                    {t("accountDeletionPublic.recovery.handoffUnavailable")}
                </div>
            )}

            {showStartForm ? (
                <form className="account-deletion-public-card__form" onSubmit={startRecovery} noValidate>
                    <label htmlFor="account-deletion-recovery-email">
                        {t("accountDeletionPublic.recovery.fields.email")}
                        <input
                            ref={emailInputRef}
                            id="account-deletion-recovery-email"
                            type="email"
                            value={email}
                            autoComplete="username"
                            onChange={(event) => setEmail(event.target.value)}
                            disabled={isBusy}
                        />
                    </label>
                    <label htmlFor="account-deletion-recovery-password">
                        {t("accountDeletionPublic.recovery.fields.password")}
                        <input
                            id="account-deletion-recovery-password"
                            type="password"
                            value={password}
                            autoComplete="current-password"
                            onChange={(event) => setPassword(event.target.value)}
                            disabled={isBusy}
                        />
                    </label>
                    <div className="account-deletion-public-card__actions">
                        <Button type="submit" disabled={isBusy}>
                            {isSubmitting
                                ? t("accountDeletionPublic.recovery.actions.starting")
                                : t("accountDeletionPublic.recovery.actions.start")}
                        </Button>
                        {ssoProviders.map((provider) => (
                            <Button
                                key={provider}
                                type="button"
                                variant="secondary"
                                disabled={isBusy}
                                onClick={() => startRecoverySso(provider)}
                            >
                                {t(`accountDeletionPublic.recovery.actions.${provider}`)}
                            </Button>
                        ))}
                    </div>
                </form>
            ) : recoveryHandoffUnavailable && step === START_STEP ? (
                <p>
                    <a href="/login">{t("accountDeletionPublic.recovery.actions.login")}</a>
                </p>
            ) : step === CHALLENGE_STEP ? (
                <form className="account-deletion-public-card__form" onSubmit={completeChallenge} noValidate>
                    <p>{t("accountDeletionPublic.recovery.challengeInstructions")}</p>
                    <label htmlFor="account-deletion-recovery-code">
                        {t("accountDeletionPublic.recovery.fields.code")}
                        <input
                            ref={codeInputRef}
                            id="account-deletion-recovery-code"
                            value={code}
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            autoComplete="one-time-code"
                            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                            disabled={isBusy}
                        />
                    </label>
                    <div className="account-deletion-public-card__actions">
                        <Button type="submit" disabled={isBusy}>
                            {isSubmitting
                                ? t("accountDeletionPublic.recovery.actions.verifying")
                                : t("accountDeletionPublic.recovery.actions.verify")}
                        </Button>
                    </div>
                </form>
            ) : (
                <RecoveryStatusPanel
                    status={recoverySession.status}
                    hasConfirmation={recoverySession.hasConfirmation}
                    isSubmitting={isSubmitting}
                    onConfirm={confirmRecovery}
                    onRefresh={refreshStatus}
                    lastCheckedAt={recoverySession.lastCheckedAt}
                    panelRef={statusRegionRef}
                />
            )}
        </AccountDeletionPublicLayout>
    );
};

export default AccountDeletionRecoveryPage;
